import React, { useEffect, useRef, useState } from 'react';
import { getAIResponse, getAIResponseFromImage } from '../../services/openaiService';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
import mammoth from 'mammoth';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  MicIcon,
  SettingsIcon,
  XIcon,
  SendIcon,
  SquareIcon,
  StopCircleIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  UserIcon,
} from '../icons';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import FreeCreditsLabel from '../../components/FreeCreditsLabel';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/* ─────────────────────────────────────────────────────────────────────────
 * AI Interview Assistant — fully Electron-native pane.
 *
 * Two views inside the same component, switched via local state:
 *   1. SetupView — clean form (job role / company / level / JD / resume).
 *   2. LiveView  — clean interview UI (timer, question input, AI streaming
 *                  response, conversation log, end session).
 *
 * The live view talks directly to the same backend endpoints the website's
 * InterviewPage uses (deduct-credit + getAIResponse) so functionality is
 * identical — only the UI is rewritten for the desktop shell.
 * ─────────────────────────────────────────────────────────────────────── */

async function extractText(file) {
  const name = (file.name || '').toLowerCase();
  if (file.type === 'text/plain' || name.endsWith('.txt')) return await file.text();
  if (file.type === 'application/pdf' || name.endsWith('.pdf')) {
    const buf = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    let out = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const c = await page.getTextContent();
      out += c.items.map(it => it.str).join(' ') + '\n';
    }
    return out.trim();
  }
  if (name.endsWith('.docx') || name.endsWith('.doc') || file.type.includes('word')) {
    const buf = await file.arrayBuffer();
    const r = await mammoth.extractRawText({ arrayBuffer: buf });
    return (r.value || '').trim();
  }
  throw new Error('Unsupported file. Use PDF, DOCX, DOC, or TXT.');
}

function buildResumeTextFromProfile(profile) {
  if (!profile) return '';
  let text = '';
  if (profile.fullName) text += `${profile.fullName}\n`;
  if (profile.jobTitle) text += `${profile.jobTitle}\n`;
  if (profile.location) text += `Location: ${profile.location}\n`;
  if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
  text += '\n';
  if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
  if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
  if (Array.isArray(profile.workExperience) && profile.workExperience.length) {
    text += 'WORK EXPERIENCE\n';
    for (const exp of profile.workExperience) {
      if (exp.position || exp.company) {
        text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
        if (exp.description) text += `  ${exp.description}\n`;
      }
    }
    text += '\n';
  }
  if (Array.isArray(profile.education) && profile.education.length) {
    text += 'EDUCATION\n';
    for (const ed of profile.education) {
      text += `${ed.degree || ''}${ed.school ? ` — ${ed.school}` : ''}${ed.year ? ` (${ed.year})` : ''}\n`;
    }
  }
  return text.trim();
}

export default function InterviewAssistantPane() {
  const [view, setView] = useState('setup'); // 'setup' | 'live'
  const [jobData, setJobData] = useState(null); // committed setup info, drives live view

  if (view === 'setup') {
    return (
      <SetupView
        onStart={(data) => { setJobData(data); setView('live'); }}
      />
    );
  }
  return (
    <LiveView
      jobData={jobData}
      onEnd={() => { setView('setup'); setJobData(null); }}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* SetupView                                                               */
/* ─────────────────────────────────────────────────────────────────────── */

function SetupView({ onStart }) {
  const [form, setForm] = useState({
    jobRole: '',
    company: '',
    experienceLevel: 'Mid-level',
    jobDescription: '',
  });
  const [resumeChoice, setResumeChoice] = useState('profile');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedText, setUploadedText] = useState('');
  const [parseError, setParseError] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [profile, setProfile] = useState({ data: null, hasResume: false, loading: true });
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const fileRef = useRef(null);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (cancelled) return;
        const profileObj = d?.profile;
        const filename = profileObj?.resumeFilename || null;
        setProfile({ data: profileObj, hasResume: !!filename, loading: false });
        if (!filename) setResumeChoice('upload');
      } catch {
        if (!cancelled) setProfile({ data: null, hasResume: false, loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    setParseError('');
    setIsParsing(true);
    try {
      const text = await extractText(file);
      if (!text) throw new Error('No text extracted from file.');
      setUploadedText(text);
    } catch (err) {
      setParseError(err.message || 'Failed to parse file.');
      setUploadedText('');
    } finally {
      setIsParsing(false);
    }
  };

  const canStart = (() => {
    if (!form.jobRole.trim() || !form.jobDescription.trim()) return false;
    if (resumeChoice === 'upload' && !uploadedText) return false;
    if (resumeChoice === 'profile' && !profile.hasResume) return false;
    return true;
  })();

  const gate = useFeatureGate('interviewAssistant');

  const handleStart = async () => {
    if (!canStart) return;
    if (gate.locked) { gate.openPaywall(); return; }
    setStarting(true);
    setError('');
    try {
      // Routes through useFeatureGate so the per-feature counter in the
      // header refreshes instantly and the paywall opens automatically
      // when the user runs out (server returns 403 → hook handles it).
      const { ok, reason } = await gate.deduct();
      if (!ok) {
        if (reason === 'no-credits') return;
        throw new Error(reason || 'Failed to start');
      }
      const resumeText = resumeChoice === 'upload'
        ? uploadedText
        : buildResumeTextFromProfile(profile.data);
      onStart({
        jobRole: form.jobRole.trim(),
        company: form.company.trim(),
        experienceLevel: form.experienceLevel,
        jobDescription: form.jobDescription.trim(),
        resumeText,
      });
    } catch (err) {
      setError(err.message || 'Could not start the interview.');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="pane">
      <div className="pane-eyebrow">
        <MicIcon size={11} /> Live Interview Copilot
      </div>
      <h1 className="pane-title">AI Interview Assistant</h1>

      <div className="pane-card">
        <div className="pane-row">
          <div className="pane-field">
            <label className="pane-label">Job role *</label>
            <input className="pane-input" placeholder="e.g. Senior Software Engineer" value={form.jobRole} onChange={update('jobRole')} />
          </div>
          <div className="pane-field">
            <label className="pane-label">Company</label>
            <input className="pane-input" placeholder="e.g. Twilio" value={form.company} onChange={update('company')} />
          </div>
        </div>

        <div className="pane-field">
          <label className="pane-label">Experience level</label>
          <select className="pane-select" value={form.experienceLevel} onChange={update('experienceLevel')}>
            <option value="Entry-level">Entry-level (0-2 yrs)</option>
            <option value="Mid-level">Mid-level (3-5 yrs)</option>
            <option value="Senior">Senior (6-10 yrs)</option>
            <option value="Staff/Principal">Staff / Principal (10+ yrs)</option>
          </select>
        </div>

        <div className="pane-field">
          <label className="pane-label">Job description *</label>
          <textarea className="pane-textarea" placeholder="Paste the JD here. The more detail, the better we can tailor every answer." value={form.jobDescription} onChange={update('jobDescription')} />
        </div>

        <div className="pane-field">
          <label className="pane-label">Resume *</label>
          {/* Resume choice cards side-by-side on the same row to save
              vertical space — they're short, no reason to stack them.
              Collapses to one column on narrow viewports. */}
          <div className="es-resume-choices">
            <ResumeChoiceCard
              checked={resumeChoice === 'profile'}
              disabled={!profile.hasResume}
              onClick={() => profile.hasResume && setResumeChoice('profile')}
              label="Use my profile resume"
              status={
                profile.loading ? <span className="es-choice-status" style={{ color: 'var(--es-text-subtle)' }}>(checking…)</span>
                : profile.hasResume ? (
                  <span className="es-choice-status ok"><CheckCircleIcon size={12} /> {profile.data?.resumeFilename}</span>
                ) : (
                  <span className="es-choice-status warn"><AlertTriangleIcon size={12} /> no resume on profile</span>
                )
              }
            />
            <ResumeChoiceCard
              checked={resumeChoice === 'upload'}
              onClick={() => setResumeChoice('upload')}
              label="Upload a different resume"
              status={uploadedFile && (
                <span className="es-choice-status ok">
                  <CheckCircleIcon size={12} /> {uploadedFile.name}
                  {isParsing && <span style={{ color: 'var(--es-text-subtle)', marginLeft: 6 }}>parsing…</span>}
                </span>
              )}
              action={resumeChoice === 'upload' && (
                <button type="button" className="es-choice-action" onClick={() => fileRef.current?.click()}>
                  {uploadedFile ? 'Replace file' : 'Choose file'}
                </button>
              )}
            />
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" onChange={onFile} style={{ display: 'none' }} />
          {parseError && <div className="pane-error">{parseError}</div>}
        </div>

        {error && <div className="pane-error">{error}</div>}
      </div>

      <button
        type="button"
        className="pane-cta"
        onClick={handleStart}
        disabled={!canStart || starting || gate.locked}
        title={gate.locked ? 'Out of free credits — upgrade to keep using' : ''}
      >
        {starting ? 'Starting…' : <>Start Interview → <FreeCreditsLabel feature="interviewAssistant" /></>}
      </button>
    </div>
  );
}

function ResumeChoiceCard({ checked, disabled, onClick, label, status, action }) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      className={`es-choice ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}`}
    >
      <span className="es-choice-radio" aria-hidden="true">
        <span className="es-choice-radio-dot" />
      </span>
      <div className="es-choice-body">
        <div className="es-choice-label">{label}</div>
        {status && <div className="es-choice-meta">{status}</div>}
        {action && <div className="es-choice-actions">{action}</div>}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* LiveView                                                                */
/* ─────────────────────────────────────────────────────────────────────── */

// Available languages for both interview transcription and AI response.
// Whisper uses ISO-639-1 codes for transcription; chat completions just need
// the human-readable name in the prompt, so we store both.
const LANGUAGES = [
  { code: 'auto', label: 'Auto detect',  name: 'Auto' },
  { code: 'en',   label: 'English',      name: 'English' },
  { code: 'es',   label: 'Spanish',      name: 'Spanish' },
  { code: 'fr',   label: 'French',       name: 'French' },
  { code: 'de',   label: 'German',       name: 'German' },
  { code: 'it',   label: 'Italian',      name: 'Italian' },
  { code: 'pt',   label: 'Portuguese',   name: 'Portuguese' },
  { code: 'hi',   label: 'Hindi',        name: 'Hindi' },
  { code: 'zh',   label: 'Chinese',      name: 'Chinese' },
  { code: 'ja',   label: 'Japanese',     name: 'Japanese' },
  { code: 'ko',   label: 'Korean',       name: 'Korean' },
  { code: 'ar',   label: 'Arabic',       name: 'Arabic' },
  { code: 'ru',   label: 'Russian',      name: 'Russian' },
  { code: 'nl',   label: 'Dutch',        name: 'Dutch' },
];

const SETTINGS_KEY = 'terview.interview.settings.v1';
const DEFAULT_SETTINGS = {
  responseLength: 'default',         // 'concise' | 'default' | 'detailed'
  interviewLanguage: 'en',           // ISO code or 'auto'
  aiResponseLanguage: 'auto',        // 'auto' or language name
  processTier: 'medium',             // 'fast' | 'medium' | 'quality'
  autoSkipNoise: true,               // ignore very brief / quiet bursts
  noiseThreshold: 0.22,              // 0.05 – 0.6 (relative volume floor)
  fontSize: 15,                      // 12 – 22 px
  hideQuestionText: false,           // privacy: hide interviewer text
  autoCopyResponse: false,           // copy AI answer to clipboard on finish
};

function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

// Whisper hallucinates on silence / background noise — it emits common
// "filler" outputs that aren't real interviewer speech. We filter these
// out BEFORE sending to askAI so we don't burn an AI call on garbage and
// so the conversation log stays clean.
const WHISPER_HALLUCINATIONS = new Set([
  'thanks for watching', 'thank you for watching', 'thanks for watching!',
  'thank you', 'thanks', 'thanks!', 'thank you.',
  'bye', 'bye.', 'bye-bye', 'goodbye', 'goodbye.',
  'hi.', 'hello.', 'hello', 'hey.', 'hey',
  'yeah', 'yeah.', 'yes', 'yes.', 'no', 'no.', 'okay', 'ok', 'ok.', 'okay.',
  'you', 'you.', 'me', 'me.', 'um', 'uh', 'mm', 'hmm', 'mhm', 'hm', 'ah', 'oh',
  'music', '[music]', '(music)', '♪', '(soft music)', '(upbeat music)',
  'silence', '[silence]', '(silence)', '[inaudible]', '(inaudible)',
  "i'm not sure", "i'm not sure.", "i don't know", "i don't know.",
  'subscribe', 'like and subscribe', 'see you next time',
]);

function isValidInterviewQuestion(transcript) {
  const text = (transcript || '').trim();
  if (!text) return false;

  // 1. Too short — anything under 15 chars is almost always noise.
  //    Real interview questions like "What is .NET?" are 14 chars; we
  //    still drop those because Whisper rarely emits valid 14-char
  //    questions cleanly — the false-positive rate from noise is far
  //    higher than the false-negative on terse real questions.
  if (text.length < 15) return false;

  // 2. Hallucination dictionary match.
  const lower = text.toLowerCase().replace(/[.,!?]+$/, '');
  if (WHISPER_HALLUCINATIONS.has(lower)) return false;

  // 3. URL — Whisper hallucinates "www.something.com" on silence.
  if (/^(https?:\/\/|www\.)/i.test(text)) return false;
  if (/^[a-z0-9-]+\.(com|org|net|io|co|ai|gov|edu)\b/i.test(text)) return false;

  // 4. Repeated identical phrases — "I'm not sure. I'm not sure. I'm not
  //    sure." is the textbook Whisper-on-silence hallucination.
  const sentences = text.split(/[.!?]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
  if (sentences.length >= 2) {
    const uniqueCount = new Set(sentences).size;
    // If 3+ sentences and ≤2 are unique, it's almost certainly a loop.
    if (sentences.length >= 3 && uniqueCount <= 2) return false;
    // If every sentence is identical (regardless of count), drop it.
    if (uniqueCount === 1) return false;
  }

  // 5. Word-level repetition — "you you you you" / "okay okay okay".
  const words = text.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length >= 3) {
    const uniqueWords = new Set(words).size;
    if (uniqueWords / words.length < 0.4) return false; // 60%+ duplicates
  }

  // 6. No alphabetic content — pure punctuation / numbers.
  if (!/[a-zA-Z]{4,}/.test(text)) return false;

  return true;
}

function LiveView({ jobData, onEnd }) {
  const [conversation, setConversation] = useState([]); // { id, question, response, streaming }
  const [questionInput, setQuestionInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(30 * 60); // 30 minute default
  const [mode, setMode] = useState('manual');             // 'manual' | 'auto'
  const [isMuted, setIsMuted] = useState(true);            // mic state for auto mode
  const [interimText, setInterimText] = useState('');       // live transcription preview

  // ── Persisted user settings (response length, languages, noise filter…) ─
  // Stored under one key in localStorage so they survive across sessions and
  // between Setup/Live transitions. Refs mirror the latest value so async
  // callbacks (transcribe, VAD tick, askAI) read the live settings without
  // re-creating the long-lived effect.
  const [settings, setSettings] = useState(loadSettings);
  const [draftSettings, setDraftSettings] = useState(settings); // edits inside the modal
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const fontSize = settings.fontSize;

  const convoRef = useRef(null);
  const inputRef = useRef(null);
  const idCounter = useRef(0);
  const conversationRef = useRef(conversation); // for fresh history inside async callbacks
  conversationRef.current = conversation;
  // Auto-mode (Whisper) refs
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const rafRef = useRef(null);
  const isProcessingRef = useRef(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [audioDevice, setAudioDevice] = useState(null); // { label, isLoopback }
  const [audioLevel, setAudioLevel] = useState(0);  // 0-100 live volume from mic
  const [isRecordingNow, setIsRecordingNow] = useState(false);
  const [autoError, setAutoError] = useState('');
  const isMutedRef = useRef(true);
  isMutedRef.current = isMuted;
  const stopRecordingRef = useRef(null); // exposes a stop function for the "Send now" button

  // Manual-mode mic recording (echo-cancelled default mic). Mirrors the
  // website's `handleMicClick`: click to start, click again to stop. On stop
  // the blob is sent to Whisper and the transcript fed into askAI.
  const [manualMicActive, setManualMicActive] = useState(false);
  const [manualMicError, setManualMicError] = useState('');
  const manualRecorderRef = useRef(null);
  const manualChunksRef = useRef([]);

  const toggleManualMic = async () => {
    if (isLoading || isTranscribing) return;
    if (!manualMicActive) {
      setManualMicError('');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 },
        });
        manualChunksRef.current = [];
        const rec = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
        rec.ondataavailable = (e) => { if (e.data.size > 0) manualChunksRef.current.push(e.data); };
        rec.onstop = async () => {
          stream.getTracks().forEach(t => t.stop());
          const chunks = manualChunksRef.current;
          manualChunksRef.current = [];
          if (!chunks.length) return;
          const blob = new Blob(chunks, { type: 'audio/webm' });
          setIsTranscribing(true);
          setQuestionInput('Transcribing…');
          try {
            const fd = new FormData();
            fd.append('audio', new File([blob], 'audio.webm', { type: 'audio/webm', lastModified: Date.now() }));
            fd.append('language', settingsRef.current.interviewLanguage || 'auto');
            const r = await fetch(`${API_BASE_URL}/api/interview-assistant/transcribe`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
              body: fd,
            });
            if (!r.ok) {
              const text = await r.text().catch(() => '');
              throw new Error(`HTTP ${r.status} ${text.slice(0, 120)}`);
            }
            const data = await r.json();
            const transcript = (data?.transcript || '').trim();
            setQuestionInput('');
            if (transcript) {
              await askAI(transcript);
            } else {
              setManualMicError('No speech detected — try recording again with clearer audio.');
            }
          } catch (err) {
            setQuestionInput('');
            setManualMicError(`Transcription failed: ${err.message}`);
          } finally {
            setIsTranscribing(false);
          }
        };
        rec.start(1000); // collect chunks every 1s
        manualRecorderRef.current = rec;
        setManualMicActive(true);
      } catch (err) {
        setManualMicError(
          err?.name === 'NotAllowedError'
            ? 'Microphone access was denied. Allow it in your system settings, then try again.'
            : `Could not access microphone: ${err.message || err}`
        );
      }
    } else {
      try { manualRecorderRef.current?.stop(); } catch { /* ignore */ }
      manualRecorderRef.current = null;
      setManualMicActive(false);
    }
  };

  // Stop manual recording if the user switches to Auto mid-recording.
  useEffect(() => {
    if (mode !== 'manual' && manualMicActive) {
      try { manualRecorderRef.current?.stop(); } catch { /* ignore */ }
      manualRecorderRef.current = null;
      setManualMicActive(false);
    }
  }, [mode, manualMicActive]);

  // Timer countdown
  useEffect(() => {
    const t = setInterval(() => setSecondsLeft(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-scroll behaviour. Two important fixes here:
  //   1. Only scrolls when a NEW message is added (length changes),
  //      NOT on every streaming token — that was fighting the user's
  //      manual scroll wheel during answer generation.
  //   2. Scrolls to the TOP (0) because the conversation now renders
  //      newest-first; the new question/answer pair lands at the top.
  const prevConvLenRef = useRef(0);
  useEffect(() => {
    if (conversation.length !== prevConvLenRef.current) {
      prevConvLenRef.current = conversation.length;
      if (convoRef.current) convoRef.current.scrollTop = 0;
    }
  }, [conversation]);

  // Focus input on mount + after each response (manual mode only).
  useEffect(() => {
    if (mode === 'manual' && !isLoading && inputRef.current) inputRef.current.focus();
  }, [isLoading, mode]);

  // Submit a question to the AI and stream the response into the conversation.
  // Reused by both manual Enter and auto-submit-on-silence flows.
  const askAI = async (q) => {
    if (!q || isLoading) return;
    const id = ++idCounter.current;
    // Prepend — newest question/answer pair sits at the TOP of the
    // conversation feed so the user always sees the most recent
    // exchange without scrolling.
    setConversation(prev => [{ id, question: q, response: '', streaming: true }, ...prev]);
    setIsLoading(true);
    try {
      // getAIResponse expects history as a flat list of { sender, message }
      // turns alternating user → ai (it maps `sender === 'ai' ? 'assistant'
      // : 'user'` and `content: msg.message`). Earlier we passed the local
      // shape `{ question, answer }` directly, which produced messages with
      // `content: undefined` after the first turn — OpenAI rejected those
      // with 400 and the bubble silently stayed empty. Flatten each Q&A
      // into two history items in the website's shape.
      const flatHistory = conversationRef.current.flatMap(c => [
        { sender: 'interviewer', message: c.question },
        { sender: 'ai',          message: c.response },
      ]);
      const s = settingsRef.current;
      // Translate the dropdown value (ISO code) into the human language name
      // the prompt expects ('English', 'Spanish'…). 'auto' stays 'auto'.
      const langEntry = LANGUAGES.find(l => l.code === s.aiResponseLanguage) || null;
      const aiLangForPrompt = !langEntry || langEntry.code === 'auto' ? 'auto' : langEntry.name;

      const finalText = await getAIResponse(
        q,
        jobData,
        flatHistory,
        (fullText) => {
          setConversation(prev =>
            prev.map(c => c.id === id ? { ...c, response: fullText } : c)
          );
        },
        {
          responseLength: s.responseLength,
          aiResponseLanguage: aiLangForPrompt,
          processTier: s.processTier,
        }
      );
      setConversation(prev =>
        prev.map(c => c.id === id ? { ...c, streaming: false } : c)
      );
      if (s.autoCopyResponse && finalText) {
        try { await navigator.clipboard.writeText(finalText); } catch { /* clipboard unavailable */ }
      }
    } catch (err) {
      setConversation(prev =>
        prev.map(c => c.id === id
          ? { ...c, response: `Failed to generate response: ${err.message}`, streaming: false, error: true }
          : c)
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Quick-prompt regenerate — re-ask the SAME question with a follow-up
  // instruction (Shorter / More technical / Add example / Use STAR) and
  // overwrite the existing response in place. Same model + history as
  // askAI, just with an appended instruction line.
  const QUICK_PROMPTS = [
    { label: 'Shorter',        mod: 'Rewrite the previous answer in 3 short sentences max.' },
    { label: 'More technical', mod: 'Make the previous answer more technical with concrete tools, metrics, and architecture.' },
    { label: 'Add example',    mod: 'Add a specific real-world example with a company, project, or measurable outcome.' },
    { label: 'Use STAR',       mod: 'Restructure the answer using the STAR method (Situation, Task, Action, Result) as flowing prose.' },
  ];
  const regenerateAnswer = async (cardId, originalQuestion, modifier) => {
    if (!originalQuestion || isLoading) return;
    setConversation(prev =>
      prev.map(c => c.id === cardId ? { ...c, response: '', streaming: true, error: false } : c)
    );
    setIsLoading(true);
    try {
      // History excludes the card we're regenerating so the model doesn't
      // see its own previous answer and try to "continue" from it.
      const flatHistory = conversationRef.current
        .filter(c => c.id !== cardId)
        .flatMap(c => [
          { sender: 'interviewer', message: c.question || '' },
          { sender: 'ai',          message: c.response || '' },
        ]);
      const s = settingsRef.current;
      const langEntry = LANGUAGES.find(l => l.code === s.aiResponseLanguage) || null;
      const aiLangForPrompt = !langEntry || langEntry.code === 'auto' ? 'auto' : langEntry.name;

      const composed = `${originalQuestion}\n\n[Follow-up instruction: ${modifier}]`;
      const finalText = await getAIResponse(
        composed,
        jobData,
        flatHistory,
        (fullText) => {
          setConversation(prev =>
            prev.map(c => c.id === cardId ? { ...c, response: fullText } : c)
          );
        },
        {
          responseLength: s.responseLength,
          aiResponseLanguage: aiLangForPrompt,
          processTier: s.processTier,
        }
      );
      setConversation(prev =>
        prev.map(c => c.id === cardId ? { ...c, streaming: false } : c)
      );
      if (s.autoCopyResponse && finalText) {
        try { await navigator.clipboard.writeText(finalText); } catch { /* clipboard unavailable */ }
      }
    } catch (err) {
      setConversation(prev =>
        prev.map(c => c.id === cardId
          ? { ...c, response: `Failed to regenerate: ${err.message}`, streaming: false, error: true }
          : c)
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Vision variant — same shape as askAI but accepts an image data URL
  // and routes through the vision-capable openaiService function. Triggered
  // by ⌘/Ctrl+Shift+S (global hotkey, main process captures the screen and
  // sends us the image via IPC) or by the camera button in the composer.
  const askAIFromImage = async (dataUrl) => {
    if (!dataUrl) {
      console.warn('[screenshot] dropped — no image data');
      return;
    }
    if (isLoading) {
      console.warn('[screenshot] dropped — already generating a response');
      alert('Wait for the current answer to finish, then capture again.');
      return;
    }
    // LiveView only renders once `jobData` is committed, so just guard
    // the prop. `view` lives in the outer InterviewAssistantPane and
    // isn't accessible here.
    if (!jobData) {
      alert('Start the interview first, then press ⌘/Ctrl+Shift+S to capture and ask about your screen.');
      return;
    }
    const id = ++idCounter.current;
    // The conversation card stores the question text in `question`.
    // We add the image alongside so the renderer can display the thumbnail.
    // Prepend so the newest screenshot lands at the top of the feed.
    setConversation(prev => [{
      id,
      question: '',
      image: dataUrl,
      kind: 'screenshot',
      response: '',
      streaming: true,
    }, ...prev]);
    setIsLoading(true);
    try {
      const flatHistory = conversationRef.current.flatMap(c => [
        { sender: 'interviewer', message: c.question || '' },
        { sender: 'ai',          message: c.response || '' },
      ]);
      const s = settingsRef.current;
      const langEntry = LANGUAGES.find(l => l.code === s.aiResponseLanguage) || null;
      const aiLangForPrompt = !langEntry || langEntry.code === 'auto' ? 'auto' : langEntry.name;

      const finalText = await getAIResponseFromImage(
        dataUrl,
        jobData,
        flatHistory,
        (fullText) => {
          setConversation(prev =>
            prev.map(c => c.id === id ? { ...c, response: fullText } : c)
          );
        },
        { processTier: s.processTier, aiResponseLanguage: aiLangForPrompt }
      );
      setConversation(prev =>
        prev.map(c => c.id === id ? { ...c, streaming: false } : c)
      );
      if (s.autoCopyResponse && finalText) {
        try { await navigator.clipboard.writeText(finalText); } catch { /* clipboard unavailable */ }
      }
    } catch (err) {
      setConversation(prev =>
        prev.map(c => c.id === id
          ? { ...c, response: `Failed to read screenshot: ${err.message}`, streaming: false, error: true }
          : c)
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Keep a ref to the latest askAIFromImage so the IPC listener (attached
  // once on mount) always calls the current closure.
  const askAIFromImageRef = useRef(askAIFromImage);
  askAIFromImageRef.current = askAIFromImage;

  // Wire the global ⌘/Ctrl+Shift+S hotkey. The main process owns the
  // OS-level shortcut + native desktopCapturer; this listener receives
  // the captured image (or permission / error notifications) over IPC.
  useEffect(() => {
    const ipc = typeof window !== 'undefined' && window.electron && window.electron.ipcRenderer;
    if (!ipc || typeof ipc.on !== 'function') return;

    ipc.on('terview:screenshot-captured', (_event, dataUrl) => {
      console.log('[screenshot] image received, length:', dataUrl?.length);
      askAIFromImageRef.current?.(dataUrl);
    });
    ipc.on('terview:screenshot-error', (_event, msg) => {
      console.warn('[screenshot] capture error:', msg);
      alert(`Screenshot capture failed: ${msg || 'unknown error'}`);
    });
    ipc.on('terview:screenshot-permission-denied', () => {
      alert(
        'Terview needs Screen Recording permission.\n\n' +
        'System Settings just opened — enable Terview under ' +
        '"Privacy & Security → Screen Recording", then restart the app.'
      );
    });

    return () => {
      if (typeof ipc.removeAllListeners === 'function') {
        ipc.removeAllListeners('terview:screenshot-captured');
        ipc.removeAllListeners('terview:screenshot-error');
        ipc.removeAllListeners('terview:screenshot-permission-denied');
      }
    };
  }, []);

  // Composer button handler — sends an IPC request to main to do the
  // native capture (same path the global hotkey uses).
  const captureScreenshotNow = () => {
    const ipc = typeof window !== 'undefined' && window.electron && window.electron.ipcRenderer;
    if (!ipc || typeof ipc.send !== 'function') {
      alert('Screenshot capture is only available in the Terview desktop app.');
      return;
    }
    ipc.send('terview:request-screenshot');
  };

  const submitQuestion = async () => {
    const q = questionInput.trim();
    if (!q) return;
    setQuestionInput('');
    await askAI(q);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitQuestion();
    }
  };

  // ── Auto mode: MediaRecorder + Whisper transcription ────────────────────
  // Mirrors the website's pipeline: capture audio with MediaRecorder, run
  // a simple voice-activity detector via AudioContext analyser, and when
  // the speaker pauses (~1s silence after at least 500ms of speech) POST
  // the recorded blob to /api/interview-assistant/transcribe. The returned
  // transcript is fed straight into askAI.
  // Web Speech Recognition was tried first but is unreliable in Electron
  // (depends on Google's web service, often returns empty silently).
  useEffect(() => {
    const cleanup = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (mediaRecorderRef.current) {
        try {
          if (mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
        } catch { /* ignore */ }
        mediaRecorderRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      if (audioContextRef.current) {
        try { audioContextRef.current.close(); } catch { /* ignore */ }
        audioContextRef.current = null;
      }
      setInterimText('');
    };

    if (mode !== 'auto' || isMuted) {
      cleanup();
      return cleanup;
    }

    let cancelled = false;
    (async () => {
      try {
        // Find a loopback audio device so we capture system audio (the
        // interviewer in a Zoom / Google Meet / Teams call), not just the
        // user's mic. Matches the website's logic:
        //   macOS  → "BlackHole" virtual device
        //   Windows → VB-Cable, VB-Audio, or Voicemeeter
        // enumerateDevices() only returns labels AFTER mic permission is
        // granted, so we do a permission-priming getUserMedia first, then
        // enumerate, then re-acquire with the loopback device if found.
        const isWindows = (navigator.platform || '').toLowerCase().includes('win');
        const loopbackName = isWindows ? 'VB-Cable / Voicemeeter' : 'BlackHole';

        const audioConstraintsBase = {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        };

        // Step 1 — prime mic permission so device labels become visible.
        let stream;
        const primer = await navigator.mediaDevices.getUserMedia({ audio: audioConstraintsBase });
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const loopback = devices.find(d => {
            if (d.kind !== 'audioinput') return false;
            const lbl = (d.label || '').toLowerCase();
            return isWindows
              ? (lbl.includes('cable output') || lbl.includes('vb-audio') || lbl.includes('voicemeeter'))
              : lbl.includes('blackhole');
          });

          // Step 2 — if a loopback exists, drop the primer and re-acquire on it.
          if (loopback) {
            primer.getTracks().forEach(t => t.stop());
            stream = await navigator.mediaDevices.getUserMedia({
              audio: { ...audioConstraintsBase, deviceId: { exact: loopback.deviceId } },
            });
            setAudioDevice({ label: loopback.label, isLoopback: true });
          } else {
            // No loopback installed — keep the primer stream as the working stream.
            const def = devices.find(d => d.kind === 'audioinput' && d.deviceId === 'default') || devices.find(d => d.kind === 'audioinput');
            setAudioDevice({ label: def?.label || `Default mic (no ${loopbackName} found)`, isLoopback: false });
            stream = primer;
          }
        } catch (enumErr) {
          // enumerate failed — at least the primer worked, use it.
          console.warn('Device enumeration failed, falling back to default mic:', enumErr);
          stream = primer;
        }

        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;

        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);
        audioContextRef.current = audioCtx;

        const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
        mediaRecorderRef.current = recorder;

        let chunks = [];
        let isRecording = false;
        let silenceTimer = null;
        let recordingTimer = null;
        let lastSoundTime = Date.now();
        // Volume threshold and min-duration are driven by user settings, read
        // live via settingsRef so changes saved in the modal take effect on
        // the next tick without restarting auto-mode. The 0–1 noiseThreshold
        // maps linearly onto the 0–60 byte-frequency average range used here.
        const SILENCE_DURATION = 1200;          // ms of quiet → stop recording
        const MAX_RECORDING_DURATION = 45000;
        const getVolumeThreshold = () =>
          Math.max(2, Math.round((settingsRef.current.noiseThreshold ?? 0.22) * 50));
        const getMinDuration = () =>
          settingsRef.current.autoSkipNoise ? 500 : 0;

        // Manual stop helper — exposed via ref for the "Send now" button.
        const stopNow = () => {
          if (isRecording && recorder.state === 'recording') {
            try { recorder.stop(); } catch { /* ignore */ }
          }
        };
        stopRecordingRef.current = stopNow;

        const buffer = new Uint8Array(analyser.frequencyBinCount);

        const tick = () => {
          if (!streamRef.current || isMutedRef.current) return;
          analyser.getByteFrequencyData(buffer);
          let sum = 0;
          for (let i = 0; i < buffer.length; i++) sum += buffer[i];
          const avg = sum / buffer.length;
          // Surface the live level so the user can see the mic is working.
          // 0-100 scale (avg is 0-255, but most speech sits in 5-60 range).
          setAudioLevel(Math.min(100, Math.round((avg / 80) * 100)));

          if (avg > getVolumeThreshold()) {
            lastSoundTime = Date.now();
            if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }

            if (!isRecording && recorder.state === 'inactive' && !isProcessingRef.current) {
              chunks = [];
              try { recorder.start(); } catch { /* race */ }
              isRecording = true;
              setIsRecordingNow(true);
              setInterimText('Recording…');
              recordingTimer = setTimeout(() => {
                if (isRecording && recorder.state === 'recording') {
                  try { recorder.stop(); } catch { /* ignore */ }
                }
              }, MAX_RECORDING_DURATION);
            }
          } else if (isRecording && !silenceTimer) {
            const recordedFor = Date.now() - (lastSoundTime - SILENCE_DURATION);
            if (recordedFor >= getMinDuration()) {
              silenceTimer = setTimeout(() => {
                if (isRecording && recorder.state === 'recording') {
                  try { recorder.stop(); } catch { /* ignore */ }
                }
              }, SILENCE_DURATION);
            }
          }
          rafRef.current = requestAnimationFrame(tick);
        };

        recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
        recorder.onstop = async () => {
          isRecording = false;
          setIsRecordingNow(false);
          if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }
          if (recordingTimer) { clearTimeout(recordingTimer); recordingTimer = null; }
          if (chunks.length === 0) { setInterimText(''); return; }
          if (isProcessingRef.current) { chunks = []; return; }

          const blob = new Blob(chunks, { type: 'audio/webm' });
          chunks = [];
          isProcessingRef.current = true;
          setIsTranscribing(true);
          setAutoError('');
          setInterimText('Transcribing…');
          try {
            const fd = new FormData();
            fd.append('audio', new File([blob], 'audio.webm', { type: 'audio/webm', lastModified: Date.now() }));
            fd.append('language', settingsRef.current.interviewLanguage || 'auto');
            const r = await fetch(`${API_BASE_URL}/api/interview-assistant/transcribe`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
              body: fd,
            });
            if (!r.ok) {
              const text = await r.text().catch(() => '');
              throw new Error(`HTTP ${r.status} ${text.slice(0, 120)}`);
            }
            const data = await r.json();
            const transcript = (data?.transcript || '').trim();
            if (transcript && isValidInterviewQuestion(transcript)) {
              setInterimText(transcript);
              await askAI(transcript);
              setInterimText('');
            } else if (transcript) {
              // Whisper returned something but it's almost certainly
              // noise (URL, repeated phrase, "you", etc.). Drop it
              // silently — don't waste an AI call.
              console.log('[auto] dropped noise transcript:', transcript);
              setInterimText('');
            } else {
              setAutoError('No speech detected in that recording. Try speaking louder or closer to the mic.');
              setInterimText('');
            }
          } catch (err) {
            setAutoError(`Transcription failed: ${err.message}`);
            setInterimText('');
          } finally {
            isProcessingRef.current = false;
            setIsTranscribing(false);
          }
        };

        rafRef.current = requestAnimationFrame(tick);
      } catch (err) {
        if (err && (err.name === 'NotAllowedError' || err.name === 'SecurityError')) {
          alert('Microphone access was denied. Allow it in your system & browser settings, then try again.');
        } else {
          alert(`Could not start auto recording: ${err.message || err}`);
        }
        setIsMuted(true);
      }
    })();

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, isMuted]);

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');
  // Timer color shifts as the session burns down: green > 5 min, amber 1-5,
  // red < 1 min. Gives the user peripheral awareness without an alert popup.
  const timerTone = secondsLeft <= 60 ? 'danger' : secondsLeft <= 300 ? 'warn' : '';

  return (
    <div className="ilv-root">
      {/* Top bar — job + timer + settings + end */}
      <div className="ilv-topbar">
        <div className="ilv-job">
          <div className="ilv-job-role">{jobData.jobRole}</div>
          {jobData.company && <div className="ilv-job-company">{jobData.company}</div>}
        </div>
        <div className={`ilv-timer ${timerTone}`}>
          <span className="ilv-timer-dot" />
          <span className="ilv-timer-text">{mm}:{ss}</span>
        </div>
        <div className="ilv-actions">
          <button
            type="button"
            className="ilv-icon-btn"
            onClick={() => { setDraftSettings(settings); setShowSettings(true); }}
            title="Settings"
            aria-label="Open settings"
          >
            <SettingsIcon size={16} />
          </button>
          <button type="button" className="ilv-end-btn" onClick={onEnd}>End session</button>
        </div>
      </div>

      {/* Control bar — mode toggle, font size, mic. Sits below the top bar
          so a long job role doesn't push these off-screen. */}
      <div className="ilv-controlbar">
        <div className="ilv-mode-toggle" role="tablist" aria-label="Question input mode">
          <button
            role="tab"
            type="button"
            className={`ilv-mode-btn ${mode === 'manual' ? 'active' : ''}`}
            aria-selected={mode === 'manual'}
            onClick={() => {
              // Switching back to manual — make sure auto-mode's audio
              // pipeline tears down by muting first. The capture effect
              // bails immediately when isMuted flips true.
              setIsMuted(true);
              setMode('manual');
            }}
          >Manual</button>
          <button
            role="tab"
            type="button"
            className={`ilv-mode-btn ${mode === 'auto' ? 'active' : ''}`}
            aria-selected={mode === 'auto'}
            onClick={() => {
              // Auto mode is the "co-pilot" experience — start listening
              // immediately so the user never has to tap a button. The
              // capture effect will auto-pick BlackHole / VB-Cable if
              // installed; otherwise it falls back to the default mic.
              setMode('auto');
              setIsMuted(false);
            }}
            title="Listen to the interviewer and auto-submit each question"
          >Auto</button>
        </div>

        <div className="ilv-font">
          <span className="ilv-font-mark" style={{ fontSize: 11 }}>A</span>
          <input
            type="range"
            min="12" max="22" step="1"
            value={fontSize}
            onChange={e => {
              const next = parseInt(e.target.value, 10);
              setSettings(s => {
                const updated = { ...s, fontSize: next };
                try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated)); } catch {}
                return updated;
              });
              setDraftSettings(s => ({ ...s, fontSize: next }));
            }}
            className="ilv-font-slider"
            aria-label="Adjust answer text size"
          />
          <span className="ilv-font-mark" style={{ fontSize: 17 }}>A</span>
        </div>

        {mode === 'auto' && (
          <button
            type="button"
            className={`ilv-mic-btn ${isMuted ? 'muted' : 'live'}`}
            onClick={() => setIsMuted(m => !m)}
            title={isMuted ? 'Click to resume listening' : 'Click to pause listening'}
          >
            <MicIcon size={14} />
            <span className="ilv-mic-label">{isMuted ? 'Paused' : 'Listening'}</span>
          </button>
        )}
      </div>

      {/* Conversation — dialogue style: interviewer (left, neutral) vs.
          Terview (right-aligned avatar, brand-tinted bubble). Reads as a
          real exchange instead of two stacked cards. */}
      <div className="ilv-convo" ref={convoRef}>
        {conversation.length === 0 && (
          <div className="ilv-empty">
            <div className="ilv-empty-icon"><MicIcon size={22} /></div>
            <h3>Ready when you are</h3>
            {mode === 'auto' ? (
              <p>
                I'm already listening — just let the interviewer talk.
                Each question gets transcribed and answered automatically
                using your resume and the role. No buttons to press.
              </p>
            ) : (
              <p>
                Type the interviewer's question below and press <kbd>Enter</kbd>.
                Terview writes your spoken answer in real time, tailored to your resume and the role.
              </p>
            )}
          </div>
        )}
        {conversation.map(c => (
          <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="ilv-msg">
              <div className="ilv-msg-avatar q" aria-hidden="true">
                <UserIcon size={14} />
              </div>
              <div className="ilv-msg-body">
                <div className="ilv-msg-author">
                  {c.kind === 'screenshot' ? 'Screenshot' : 'Interviewer'}
                </div>
                <div className="ilv-msg-bubble" style={{ fontSize: `${fontSize}px` }}>
                  {/* Screenshot thumbnail — small inline preview so the
                      user can see what was actually captured and sent
                      to the model. Click to open the full image in a
                      new window for a closer look. */}
                  {c.image && (
                    <a
                      href={c.image}
                      target="_blank"
                      rel="noreferrer"
                      className="ilv-msg-shot"
                      title="Open full screenshot"
                    >
                      <img src={c.image} alt="Captured screenshot" />
                    </a>
                  )}
                  {settings.hideQuestionText
                    ? <em className="ilv-msg-hidden">Question hidden — privacy mode on</em>
                    : (c.question || (c.kind === 'screenshot' && !c.image
                        ? <em className="ilv-msg-hidden">Captured screenshot</em>
                        : c.question))}
                </div>
              </div>
            </div>
            <div className="ilv-msg">
              <div className="ilv-msg-avatar a" aria-hidden="true">T</div>
              <div className="ilv-msg-body">
                <div className="ilv-msg-author a">Terview · Suggested answer</div>
                <div
                  className={`ilv-msg-bubble a ${c.error ? 'error' : ''}`}
                  style={{ fontSize: `${fontSize}px` }}
                >
                  {c.error
                    ? c.response
                    : <AIMarkdown text={c.response} />}
                  {c.streaming && <span className="ilv-msg-stream-cursor" />}
                </div>
                {/* Quick-prompt chips — only after the answer finishes
                    streaming, and only for real (non-error, non-image)
                    text answers where re-asking makes sense. */}
                {!c.streaming && !c.error && c.response && c.question && (
                  <div className="ilv-msg-chips">
                    {QUICK_PROMPTS.map(qp => (
                      <button
                        key={qp.label}
                        type="button"
                        className="ilv-msg-chip"
                        onClick={() => regenerateAnswer(c.id, c.question, qp.mod)}
                        disabled={isLoading}
                        title={`Regenerate this answer — ${qp.label}`}
                      >
                        {qp.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Composer — Manual = textarea + mic + Get answer button; Auto = live
          transcription preview that auto-submits after a brief silence. */}
      {mode === 'manual' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="ilv-composer">
            <textarea
              ref={inputRef}
              className="ilv-composer-input"
              placeholder={manualMicActive
                ? 'Recording… click the mic to stop and transcribe'
                : 'Paste/type the interviewer\'s question, or click the mic to record it.'}
              value={questionInput}
              onChange={e => setQuestionInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={isLoading || manualMicActive || isTranscribing}
            />
            <button
              type="button"
              className={`ilv-manual-mic ${manualMicActive ? 'recording' : ''}`}
              onClick={toggleManualMic}
              disabled={isLoading || isTranscribing}
              title={manualMicActive ? 'Stop & transcribe' : 'Record interviewer question'}
              aria-label={manualMicActive ? 'Stop recording' : 'Start recording'}
            >
              {manualMicActive ? <SquareIcon size={16} /> : <MicIcon size={18} />}
            </button>
            <button
              type="button"
              className="ilv-composer-send"
              onClick={submitQuestion}
              disabled={isLoading || manualMicActive || !questionInput.trim()}
            >
              {isLoading ? 'Thinking…' : <>Get answer <SendIcon size={13} /></>}
            </button>
          </div>
          {manualMicError && (
            <div className="pane-error" style={{ marginTop: 0 }}>
              {manualMicError}
            </div>
          )}
        </div>
      ) : (
        <div className="ilv-auto-composer">
          {isMuted ? (
            <div className="ilv-auto-hint">
              <span className="ilv-auto-hint-icon"><MicIcon size={16} /></span>
              <span>
                Paused. Click <strong>Paused</strong> above to resume — I'll pick up the next question automatically.
              </span>
            </div>
          ) : (
            <div className="ilv-auto-listening">
              {/* Single compact row: bars · status text (truncates) ·
                  device label · Send-now button. Keeps the footer tight. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="ilv-listen-bars" aria-hidden="true">
                  <span /><span /><span /><span /><span />
                </div>
                <div
                  className={`ilv-auto-text ${interimText ? '' : 'placeholder'}`}
                  style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {interimText
                    || (isRecordingNow ? 'Recording…' : 'Listening…')}
                  {isTranscribing && <span style={{ color: 'var(--es-warning)', marginLeft: 8 }}>· transcribing</span>}
                  {isLoading && <span style={{ color: 'var(--es-success)', marginLeft: 8 }}>→ generating</span>}
                </div>
                {audioDevice && (
                  <div
                    style={{
                      fontSize: 10.5,
                      color: audioDevice.isLoopback ? 'var(--es-success)' : 'var(--es-warning)',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      flexShrink: 0,
                      maxWidth: 180,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                    title={audioDevice.label}
                  >
                    {audioDevice.isLoopback
                      ? <CheckCircleIcon size={10} />
                      : <AlertTriangleIcon size={10} />}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{audioDevice.label}</span>
                  </div>
                )}
                {isRecordingNow && (
                  <button
                    type="button"
                    className="ilv-send-now"
                    onClick={() => stopRecordingRef.current?.()}
                  >
                    <StopCircleIcon size={13} /> Send now
                  </button>
                )}
              </div>
              {/* Thin live audio level bar — direct confirmation that
                  the loopback / mic is producing signal. */}
              <div className="ilv-level-track">
                <div
                  className={`ilv-level-fill ${audioLevel > 12 ? '' : 'silent'}`}
                  style={{ width: `${audioLevel}%` }}
                />
              </div>
              {autoError && (
                <div className="pane-error" style={{ marginTop: 0 }}>
                  {autoError}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Settings drawer */}
      {showSettings && (
        <div className="ilv-modal-bg" onClick={() => setShowSettings(false)}>
          <div className="ilv-modal ilv-settings-modal" onClick={e => e.stopPropagation()}>
            <div className="ilv-modal-head">
              <h3>Settings</h3>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="ilv-modal-close"
                aria-label="Close"
              >
                <XIcon size={18} />
              </button>
            </div>
            <div className="ilv-modal-body ilv-settings-body">

              <SettingField label="Response length" hint="How long the AI's spoken answer should be.">
                <SegmentedControl
                  value={draftSettings.responseLength}
                  onChange={(v) => setDraftSettings(s => ({ ...s, responseLength: v }))}
                  options={[
                    { value: 'concise',  label: 'Concise' },
                    { value: 'default',  label: 'Default' },
                    { value: 'detailed', label: 'Detailed' },
                  ]}
                />
              </SettingField>

              <SettingField label="Interview language" hint="Language the interviewer is speaking. Improves transcription accuracy.">
                <select
                  className="ilv-select"
                  value={draftSettings.interviewLanguage}
                  onChange={e => setDraftSettings(s => ({ ...s, interviewLanguage: e.target.value }))}
                >
                  {LANGUAGES.map(l => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </SettingField>

              <SettingField label="AI response language" hint="Language the suggested answer is written in. Auto matches the interviewer.">
                <select
                  className="ilv-select"
                  value={draftSettings.aiResponseLanguage}
                  onChange={e => setDraftSettings(s => ({ ...s, aiResponseLanguage: e.target.value }))}
                >
                  {LANGUAGES.map(l => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </SettingField>

              <SettingField label="Process time" hint="Quality uses a stronger model — slower but better answers.">
                <SegmentedControl
                  value={draftSettings.processTier}
                  onChange={(v) => setDraftSettings(s => ({ ...s, processTier: v }))}
                  options={[
                    { value: 'fast',    label: 'Fast' },
                    { value: 'medium',  label: 'Medium' },
                    { value: 'quality', label: 'Quality' },
                  ]}
                />
              </SettingField>

              <SettingField label="Auto-skip noise" hint="Drop very short bursts (background noise, throat-clears) before transcribing.">
                <SegmentedControl
                  value={draftSettings.autoSkipNoise ? 'on' : 'off'}
                  onChange={(v) => setDraftSettings(s => ({ ...s, autoSkipNoise: v === 'on' }))}
                  options={[
                    { value: 'on',  label: 'Enable' },
                    { value: 'off', label: 'Disable' },
                  ]}
                />
              </SettingField>

              <SettingField label="Noise threshold" hint="Lower = more sensitive (catches quiet speech). Higher = stricter (skips more background sound).">
                <div className="ilv-slider-row">
                  <input
                    type="range"
                    min="0.05"
                    max="0.6"
                    step="0.01"
                    value={draftSettings.noiseThreshold}
                    onChange={e => setDraftSettings(s => ({ ...s, noiseThreshold: parseFloat(e.target.value) }))}
                    className="ilv-range"
                    disabled={!draftSettings.autoSkipNoise}
                  />
                  <span className="ilv-slider-value">{draftSettings.noiseThreshold.toFixed(2)}</span>
                </div>
              </SettingField>

              <SettingField label="Font size" hint="Size of the AI response and conversation text.">
                <div className="ilv-slider-row">
                  <input
                    type="range"
                    min="12"
                    max="22"
                    step="1"
                    value={draftSettings.fontSize}
                    onChange={e => setDraftSettings(s => ({ ...s, fontSize: parseInt(e.target.value, 10) }))}
                    className="ilv-range"
                  />
                  <span className="ilv-slider-value">{draftSettings.fontSize}px</span>
                </div>
              </SettingField>

              <SettingField label="Privacy" hint="Useful when you're screen-sharing during the interview.">
                <div className="ilv-toggle-stack">
                  <ToggleRow
                    label="Hide interviewer question text"
                    checked={draftSettings.hideQuestionText}
                    onChange={(v) => setDraftSettings(s => ({ ...s, hideQuestionText: v }))}
                  />
                  <ToggleRow
                    label="Auto-copy AI response to clipboard"
                    checked={draftSettings.autoCopyResponse}
                    onChange={(v) => setDraftSettings(s => ({ ...s, autoCopyResponse: v }))}
                  />
                </div>
              </SettingField>

              <div className="ilv-settings-divider" />

              <div className="ilv-job-summary">
                <div className="ilv-job-summary-head">Job details</div>
                <Detail label="Position" value={jobData.jobRole} />
                {jobData.company && <Detail label="Company" value={jobData.company} />}
                <Detail label="Experience" value={jobData.experienceLevel} />
                <Detail label="Job description" value={jobData.jobDescription} multiline />
              </div>
            </div>

            <div className="ilv-settings-foot">
              <button
                type="button"
                className="ilv-settings-reset"
                onClick={() => setDraftSettings(DEFAULT_SETTINGS)}
              >
                Reset to defaults
              </button>
              <button
                type="button"
                className="ilv-settings-save"
                onClick={() => {
                  setSettings(draftSettings);
                  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(draftSettings)); } catch {}
                  setShowSettings(false);
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── AI response renderer ─────────────────────────────────────────────────
// Streams of AI text routinely contain fenced ```lang code blocks plus
// surrounding prose. Without this, the raw text is dropped into a div and
// the browser interprets characters like `<iostream>` or `<<` as HTML tags
// — silently eating tokens. Render through ReactMarkdown so:
//   • Prose stays as paragraphs / lists outside the code box.
//   • Code blocks render in a dark IDE-style box with line numbers,
//     a language label, and a copy button.
//   • '<' '>' '&' are safely escaped, so the original code is preserved.

function PaneCodeBlock({ language, value }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <div className="terview-code-block">
      <div className="terview-code-header">
        <span className="terview-code-lang">{language || 'code'}</span>
        <button type="button" className="terview-code-copy" onClick={handleCopy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <SyntaxHighlighter
        language={language || 'text'}
        style={oneDark}
        showLineNumbers
        wrapLongLines
        customStyle={{
          margin: 0,
          padding: '14px 16px',
          background: '#0d1117',
          fontSize: '0.88em',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
        codeTagProps={{ style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' } }}
      >
        {value.replace(/\n$/, '')}
      </SyntaxHighlighter>
    </div>
  );
}

const paneMarkdownComponents = {
  code({ inline, className, children }) {
    const match = /language-(\w+)/.exec(className || '');
    const value = String(children ?? '');
    if (!inline && (match || value.includes('\n'))) {
      return <PaneCodeBlock language={match ? match[1] : ''} value={value} />;
    }
    return <code className="terview-inline-code">{children}</code>;
  },
  p:  ({ children }) => <p className="terview-md-p">{children}</p>,
  ul: ({ children }) => <ul className="terview-md-ul">{children}</ul>,
  ol: ({ children }) => <ol className="terview-md-ol">{children}</ol>,
  li: ({ children }) => <li className="terview-md-li">{children}</li>,
  h1: ({ children }) => <h3 className="terview-md-h">{children}</h3>,
  h2: ({ children }) => <h3 className="terview-md-h">{children}</h3>,
  h3: ({ children }) => <h3 className="terview-md-h">{children}</h3>,
};

function AIMarkdown({ text }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={paneMarkdownComponents}>
      {text || ''}
    </ReactMarkdown>
  );
}

function Detail({ label, value, multiline }) {
  return (
    <div className="ilv-detail">
      <div className="ilv-detail-label">{label}</div>
      <div className="ilv-detail-value" style={multiline ? { whiteSpace: 'pre-wrap', maxHeight: 220, overflowY: 'auto' } : {}}>
        {value}
      </div>
    </div>
  );
}

function SettingField({ label, hint, children }) {
  return (
    <div className="ilv-setting">
      <div className="ilv-setting-label">{label}</div>
      {hint && <div className="ilv-setting-hint">{hint}</div>}
      <div className="ilv-setting-control">{children}</div>
    </div>
  );
}

function SegmentedControl({ value, onChange, options }) {
  return (
    <div className="ilv-segmented" role="group">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={`ilv-segment ${value === opt.value ? 'active' : ''}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <label className="ilv-toggle-row">
      <span className="ilv-toggle-text">{label}</span>
      <span
        className={`ilv-toggle ${checked ? 'on' : ''}`}
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onChange(!checked); } }}
      >
        <span className="ilv-toggle-knob" />
      </span>
    </label>
  );
}
